import { useInfiniteQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { MINUTE_IN_MS } from '@/modules/shared/constants/time'
import { getDrivers } from './getDrivers'
import { PaginatedDriversResponse, Driver } from './types'

type Props = {
  companyId: string
  pageSize?: number
  groupId?: number
  isActive?: boolean
  search?: string
  enabled?: boolean
}

/**
 * Paginated drivers list using react-query's `useInfiniteQuery`.
 *
 * Replaces the hand-rolled `[page, setPage]` + data-appending useEffect
 * that used to live in `DriversContext`. Exposes:
 *   - `drivers`: flattened list of every loaded page
 *   - `totalCount` (from latest page)
 *   - `hasNextPage` / `fetchNextPage` / `isFetchingNextPage` — native
 *     infinite-scroll primitives instead of bespoke `hasMore`/`loadMore`.
 */
export function useInfiniteQueryDrivers({
  companyId,
  pageSize = 20,
  groupId,
  isActive,
  search,
  enabled = true,
}: Props) {
  const query = useInfiniteQuery<PaginatedDriversResponse>({
    queryKey: [
      'drivers-infinite',
      companyId,
      pageSize,
      groupId,
      isActive,
      search,
    ],
    enabled: enabled && !!companyId,
    initialPageParam: 1,
    staleTime: MINUTE_IN_MS * 5,
    queryFn: async ({ pageParam }) => {
      const { data } = await getDrivers({
        companyId,
        page: pageParam as number,
        pageSize,
        groupId,
        isActive,
        search,
      })
      // `getDrivers` can return the non-paginated shape when neither `page`
      // nor `pageSize` is passed, but we always pass them here.
      return data as PaginatedDriversResponse
    },
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce(
        (sum, p) => sum + (p.drivers?.length ?? 0),
        0,
      )
      return loaded < (lastPage.totalCount ?? 0) ? allPages.length + 1 : undefined
    },
  })

  const drivers: Driver[] = useMemo(
    () => query.data?.pages.flatMap((p) => p.drivers ?? []) ?? [],
    [query.data],
  )
  const totalCount = query.data?.pages.at(-1)?.totalCount ?? 0

  return { ...query, drivers, totalCount }
}
